// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState } from "@stoneage/App/Store/Reducers";
import { getDataLayer, trackAsImpersonation } from "@stoneage/App/Utilities/DataLayer";

type CartLineData = Pick<CartLineModel, "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties">;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    if (state.xGoogleAnalytics.addToCartEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult.shortDescription || "",
                    erpNumber: props.apiResult.erpNumber || "",
                    pricing: props.apiResult.pricing || null,
                    qtyOrdered: props.apiResult.qtyOrdered || null,
                    properties: props.apiResult.properties,
                },
            ];
        }

        const googleAnalyticsData = {
            event: "add_to_cart",
            currency: state.context.session.currency?.currencyCode,
            ...(props.apiResult?.pricing && { value: props.apiResult.pricing.extendedUnitNetPrice }),
            items: cartLinesToAdd.map(cartLine => ({
                item_name: cartLine.shortDescription,
                item_id: cartLine.erpNumber,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: state.xGoogleAnalytics.includeProductBrand
                    ? (cartLine as CartLineModel).brand?.name || ""
                    : undefined,
                item_category: state.xGoogleAnalytics.includeProductCategory
                    ? cartLine.properties?.["category"] || ""
                    : undefined,
                item_list_id: state.xGoogleAnalytics.includeProductCategory
                    ? cartLine.properties?.["categoryId"] || ""
                    : undefined,
            })),
            impersonation: trackAsImpersonation(state),
        };

        dataLayer.push(googleAnalyticsData);
    }
};

const TrackGaAddProductToCart = XngGaAddProductToCart as any;
addToEndOfChain(AddToCartChain, TrackGaAddProductToCart);
addToEndOfChain(AddWishListToCartChain, TrackGaAddProductToCart);
addToEndOfChain(AddCartLinesChain, TrackGaAddProductToCart);
addToEndOfChain(AddLinesToCartChain, TrackGaAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, TrackGaAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, TrackGaAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, TrackGaAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, TrackGaAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, TrackGaAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, TrackGaAddProductToCart);
